import React, { useState, useEffect } from 'react';
import { Container, Button, Box, CircularProgress, Alert, Typography } from '@mui/material';
import CipherChallenge from '../components/CipherChallenge';
import { getRandomShortQuote, hillEncode, generateHillKey, hillDecode } from '../utils/cipherUtils';

const HillCipher = () => {
  const [challenge, setChallenge] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [key, setKey] = useState(null);

  const generateNewChallenge = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const quote = await getRandomShortQuote();
      if (quote) {
        // Generate a random 2x2 or 3x3 key matrix
        const matrixSize = Math.random() < 0.5 ? 2 : 3;
        const newKey = generateHillKey(matrixSize);
        setKey(newKey);
        
        // Calculate padding letters and encode the message
        const cleanText = quote.text.replace(/[^a-zA-Z]/g, '').toUpperCase();
        const paddingCount = matrixSize - (cleanText.length % matrixSize);
        const paddingLetters = paddingCount < matrixSize ? 'X'.repeat(paddingCount) : '';
        const textWithPadding = cleanText + paddingLetters;
        const encodedMessage = hillEncode(textWithPadding, newKey);
        
        // Calculate the decoded padding letters
        const decodedPadding = hillDecode(encodedMessage, newKey).slice(cleanText.length);
        
        setChallenge({
          encodedMessage: encodedMessage,
          correctAnswer: (quote.text.replace(/[^a-zA-Z]/g, '') + decodedPadding).toLowerCase(),
          originalText: quote.text,
          hint: `This is a Hill cipher using a ${matrixSize}x${matrixSize} matrix. Each block of ${matrixSize} letters is transformed using matrix multiplication modulo 26.`,
          difficulty: quote.difficulty,
          paddingLetters: decodedPadding.toLowerCase(),
          matrixSize: matrixSize
        });
      }
    } catch (error) {
      console.error('Error generating challenge:', error);
      setError('Failed to load challenge. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    generateNewChallenge();
  }, []);

  const handleComplete = (result) => {
    if (result.success) {
      // If moveToNext is true, automatically generate a new challenge
      if (result.moveToNext) {
        generateNewChallenge();
      }
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          <Button
            variant="contained"
            color="primary"
            onClick={generateNewChallenge}
            sx={{ mt: 2 }}
          >
            Try Again
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      {challenge && (
        <>
          <CipherChallenge
            title="Hill Cipher Challenge"
            description="The Hill cipher uses matrix multiplication to transform blocks of letters. Each block is multiplied by a key matrix modulo 26 to produce the ciphertext."
            encodedMessage={challenge.encodedMessage}
            correctAnswer={challenge.correctAnswer}
            originalText={challenge.originalText}
            hint={challenge.hint}
            difficulty={challenge.difficulty}
            onComplete={handleComplete}
            autoFillSameLetters={false}
            challenge={challenge}
          />
          {key && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>
                Key Matrix:
              </Typography>
              <Box component="pre" sx={{ 
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all'
              }}>
                {key.map(row => row.join(' ')).join('\n')}
              </Box>
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default HillCipher; 